// Small helper: trigger an authenticated file download (e.g. quote PDFs).
//
// Why this exists: the previous code used plain `<a href={API}/quotes/{id}/pdf>`
// which opens the URL in a new tab. The browser does NOT include the JWT
// Authorization header for that navigation, so the backend returns:
//   { "detail": "Not authenticated" }
//
// This helper fetches via axios (which DOES carry the Authorization header
// via the request interceptor), converts the response to a blob, then triggers
// a programmatic download. Works for both authenticated and public URLs.
//
// Usage:
//   <Button onClick={() => downloadAuthedFile(`/quotes/${id}/pdf`, `quote-${num}.pdf`)}>
//       PDF
//   </Button>

import api from '@/lib/api';
import { toast } from 'sonner';

export async function downloadAuthedFile(apiPath, filename = 'download.pdf') {
    try {
        const resp = await api.get(apiPath, { responseType: 'blob' });
        const blob = resp.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Free memory next tick — gives the click handler time to start the download
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return true;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
            toast.error('Session expired. Please sign in again.');
        } else if (status === 404) {
            toast.error('File not found.');
        } else {
            toast.error('Couldn\u2019t download the file. Please try again.');
        }
        return false;
    }
}
